import React, { useState, useEffect } from 'react';
import { CompositeView, SimpleView, GridView, FilterWidget, Widget, Button } from '@digital/ui';
import { Modal, Pagination, Select, Table, Tooltip } from 'antd';
import { SearchButton } from '@app/components/FormButtons';
import {
  getNotificacoesGeral,
  exportarNotificacoes,
  NotificacaoPendenciaGeralItem,
  PaginationResult,
} from '../../api/pendenciaDocumental';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  Pendente: { label: 'Pendente', color: '' },
  PendenteEnvioEmail: { label: 'Pendente de Envio', color: 'orange' },
  PendenteEnvioFornecedorAssinado: { label: 'Pendente de Envio (Assinado)', color: 'orange' },
  PendenteEnvioReprovacaoGestor: { label: 'Pendente de Envio (Reprovada)', color: 'orange' },
  ProcessandoEnvioEmail: { label: 'Processando Envio', color: 'blue' },
  ErroEnvioFornecedor: { label: 'Erro no Envio ao Fornecedor', color: 'red' },
  ErroEnvioGestor: { label: 'Erro no Envio ao Gestor', color: 'red' },
  ErroEnvioFornecedorAssinado: { label: 'Erro no Envio (Assinado)', color: 'red' },
  ErroEnvioReprovacaoGestor: { label: 'Erro no Envio (Reprovada)', color: 'red' },
  FalhaEnvioEmail: { label: 'Falha no Envio', color: 'red' },
  PendenteAprovacao: { label: 'Pendente de Aprovação', color: 'gold' },
  Aprovada: { label: 'Aprovada pelo Gestor', color: 'green' },
  AguardandoAssinatura: { label: 'Aguardando Assinatura', color: 'purple' },
  AssinaturaExpirada: { label: 'Assinatura Expirada', color: 'red' },
  AssinaturaRecusada: { label: 'Assinatura Recusada', color: 'red' },
  Pausada: { label: 'Notificação Pausada', color: 'orange' },
  NaoNotificado: { label: 'Não Notificado', color: '' },
  Reprovada: { label: 'Reprovada', color: 'red' },
  EnviadaFornecedor: { label: 'Enviada ao Fornecedor', color: 'cyan' },
  EnviadaDireta: { label: 'Enviada Diretamente', color: 'cyan' },
};

const STATUS_OPTIONS = Object.keys(STATUS_LABELS);

const NotificacoesGeral = () => {
  const [paginacao, setPaginacao] = useState<PaginationResult<NotificacaoPendenciaGeralItem> | null>(null);
  const [numerosContrato, setNumerosContrato] = useState<number[]>([]);
  const [administradoresContrato, setAdministradoresContrato] = useState<string[]>([]);
  const [competencias, setCompetencias] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [motivoReprovacao, setMotivoReprovacao] = useState<string | null>(null);
  const [pageAtual, setPageAtual] = useState(1);
  const [sizeAtual, setSizeAtual] = useState(20);

  const [filtroContrato, setFiltroContrato] = useState<number | undefined>(undefined);
  const [filtroCompetencia, setFiltroCompetencia] = useState<string | undefined>(undefined);
  const [filtroStatus, setFiltroStatus] = useState<string | undefined>(undefined);
  const [filtroAdmContrato, setFiltroAdmContrato] = useState<string | undefined>(undefined);

  const buscar = (contrato: number | undefined, competencia: string | undefined, status: string | undefined, admContrato: string | undefined, page: number, size: number) => {
    setLoading(true);
    getNotificacoesGeral({ numeroContrato: contrato, competencia: competencia, status: status, administradorContrato: admContrato, page: page, size: size })
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        if (data) {
          setPaginacao(data.notificacoes || null);
          if (data.numerosContrato && Array.isArray(data.numerosContrato)) {
            setNumerosContrato(data.numerosContrato);
          }
          if (data.administradoresContrato && Array.isArray(data.administradoresContrato)) {
            setAdministradoresContrato(data.administradoresContrato);
          }
          if (data.competencias && Array.isArray(data.competencias)) {
            setCompetencias(data.competencias);
          }
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    buscar(undefined, undefined, undefined, undefined, 1, 20);
  }, []);

  const handleFiltrar = () => {
    setPageAtual(1);
    buscar(filtroContrato, filtroCompetencia, filtroStatus, filtroAdmContrato, 1, sizeAtual);
  };

  const handleExportar = () => {
    setExportando(true);
    exportarNotificacoes({ numeroContrato: filtroContrato, competencia: filtroCompetencia, status: filtroStatus, administradorContrato: filtroAdmContrato })
      .finally(function() { setExportando(false); });
  };

  const handleLimpar = () => {
    setFiltroContrato(undefined);
    setFiltroCompetencia(undefined);
    setFiltroStatus(undefined);
    setFiltroAdmContrato(undefined);
    setPageAtual(1);
    buscar(undefined, undefined, undefined, undefined, 1, sizeAtual);
  };

  const handleTableChange = (p: any) => {
    setPageAtual(p.current);
    setSizeAtual(p.pageSize);
    buscar(filtroContrato, filtroCompetencia, filtroStatus, filtroAdmContrato, p.current, p.pageSize);
  };

  const items = paginacao ? paginacao.items : [];

  const columns = [
    {
      title: 'Nº Contrato',
      dataIndex: 'numeroContrato',
      key: 'numeroContrato',
      width: 110,
      render: (v: number) => (v != null ? v : '-'),
    },
    {
      title: 'Competência',
      dataIndex: 'competenciaMonitoramento',
      key: 'competenciaMonitoramento',
      width: 120,
    },
    {
      title: 'Nº Protocolo',
      dataIndex: 'numeroProtocolo',
      key: 'numeroProtocolo',
      width: 160,
      render: (v: string) => v || '-',
    },
    {
      title: 'Data Envio Fornecedor',
      dataIndex: 'dataEnvioFornecedor',
      key: 'dataEnvioFornecedor',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleDateString('pt-BR') : '-'),
    },
    {
      title: 'Data Notif. Gestor',
      dataIndex: 'dataNotificacaoGestor',
      key: 'dataNotificacaoGestor',
      width: 150,
      render: (v: string) => (v ? new Date(v).toLocaleDateString('pt-BR') : '-'),
    },
    {
      title: 'Tipo Notificação',
      dataIndex: 'tipoCarta',
      key: 'tipoCarta',
      width: 110,
      render: (v: string) => v || '-',
    },
    {
      title: 'Documentos Críticos',
      dataIndex: 'temDocumentoCritico',
      key: 'temDocumentoCritico',
      width: 150,
      render: (v: boolean, row: NotificacaoPendenciaGeralItem) =>
        row.statusNotificacao === 'Pendente' || row.statusNotificacao === 'NaoNotificado'
          ? '-'
          : (v ? 'Sim' : 'Não'),
    },
    {
      title: 'Administrador de Contrato',
      dataIndex: 'administradorContrato',
      key: 'administradorContrato',
      width: 200,
      render: (v: string) => v || '-',
    },
    {
      title: 'Status',
      dataIndex: 'statusNotificacao',
      key: 'statusNotificacao',
      width: 190,
      render: (v: string, row: NotificacaoPendenciaGeralItem) => {
        const s = STATUS_LABELS[v];
        const destacar = v === 'Reprovada';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: destacar && s ? s.color : undefined }}>{s ? s.label : v}</span>
            {row.comentarioReprovacao && (
              <Tooltip title="Visualizar motivo da reprovação">
                <Button
                  type="link"
                  icon="messaging-comments"
                  onClick={() => setMotivoReprovacao(row.comentarioReprovacao)}
                  style={{ padding: 0, minWidth: 'auto' }}
                />
              </Tooltip>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <CompositeView>
      <SimpleView>
        <GridView onSubmit={handleFiltrar}>
          <FilterWidget
            active={[]}
            actions={[
              <SearchButton type="primary" key="buscar" onClick={handleFiltrar} />,
              <Button key="limpar" onClick={handleLimpar} style={{ marginLeft: 8 }}>Limpar</Button>,
              <Button key="exportar" onClick={handleExportar} loading={exportando} style={{ marginLeft: 8 }} icon="download">Exportar</Button>,
            ]}
            title="Filtros">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 0' }}>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Nº Contrato</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Todos"
                  style={{ width: 140 }}
                  value={filtroContrato}
                  onChange={(v: any) => setFiltroContrato(v !== undefined ? Number(v) : undefined)}>
                  {numerosContrato.map(n => (
                    <Select.Option key={n} value={n}>{n}</Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Competência</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Todos"
                  style={{ width: 140 }}
                  value={filtroCompetencia}
                  onChange={(v: any) => setFiltroCompetencia(v || undefined)}>
                  {competencias.map(c => (
                    <Select.Option key={c} value={c}>{c}</Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Status</div>
                <Select
                  allowClear
                  placeholder="Todos"
                  style={{ width: 200 }}
                  value={filtroStatus}
                  onChange={(v: any) => setFiltroStatus(v || undefined)}>
                  {STATUS_OPTIONS.map(k => (
                    <Select.Option key={k} value={k}>{STATUS_LABELS[k].label}</Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>Adm. de Contrato</div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Todos"
                  style={{ width: 220 }}
                  value={filtroAdmContrato}
                  onChange={(v: any) => setFiltroAdmContrato(v || undefined)}>
                  {administradoresContrato.map(n => (
                    <Select.Option key={n} value={n}>{n}</Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </FilterWidget>
          <Widget title={'Notificações de Pendências Documentais' + (paginacao ? ' — ' + paginacao.totalItems + ' registros' : '')}>
            <Table
              className="no-bold-table"
              dataSource={items}
              columns={columns}
              rowKey="id"
              size="small"
              loading={loading}
              scroll={{ x: 1360 }}
              pagination={false}
              locale={{ emptyText: 'Nenhuma notificação encontrada.' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginTop: 16 }}>
              <Pagination
                current={pageAtual}
                pageSize={sizeAtual}
                total={paginacao ? paginacao.totalItems : 0}
                showSizeChanger
                pageSizeOptions={['10', '20', '50']}
                onChange={(page: number, pageSize?: number) => handleTableChange({ current: page, pageSize: pageSize || sizeAtual })}
              />
            </div>
          </Widget>
          <Modal
            title="Motivo da reprovação"
            visible={motivoReprovacao !== null}
            onCancel={() => setMotivoReprovacao(null)}
            footer={[
              <Button key="fechar" onClick={() => setMotivoReprovacao(null)}>
                Fechar
              </Button>,
            ]}
          >
            <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{motivoReprovacao}</p>
          </Modal>
        </GridView>
      </SimpleView>
    </CompositeView>
  );
};

export default NotificacoesGeral;
